// ============================================================
// recurrenceRule.ts
// Frontend helpers for the appointment "Repeats" control — build the
// recurrenceRule JSON the form saves, parse it back for the edit form, and
// derive the recurrenceEndAt bound. The JSON SHAPE is shared with the
// backend expander (backend/src/lib/recurrence.ts) — keep the two in sync.
// Occurrence EXPANSION lives on the backend only; the frontend just
// authors/reads the rule.
// ============================================================
import { RecurrenceRule, RecurrenceFreq, Weekday, RecurrenceEnds } from '../types/calendar.types'

export const WEEKDAYS: { code: Weekday; label: string }[] = [
  { code: 'MO', label: 'Mon' },
  { code: 'TU', label: 'Tue' },
  { code: 'WE', label: 'Wed' },
  { code: 'TH', label: 'Thu' },
  { code: 'FR', label: 'Fri' },
  { code: 'SA', label: 'Sat' },
  { code: 'SU', label: 'Sun' },
]

// getDay() (Sun=0..Sat=6) → weekday code, for defaulting weekly byday to the
// appointment's own date.
const DOW_TO_CODE: Weekday[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
export const weekdayCodeForDate = (dateStr: string): Weekday | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return DOW_TO_CODE[d.getDay()]
}

// Build the rule JSON string from the form's selections. Returns null for a
// non-repeating appointment (freq === null) → the appointment is a one-off.
export const buildRecurrenceRule = (
  freq: RecurrenceFreq | null,
  byday: Weekday[],
  ends: RecurrenceEnds,
): string | null => {
  if (!freq) return null
  const rule: RecurrenceRule = {
    freq,
    interval: freq === 'fortnightly' ? 2 : 1,
    ends: normaliseEnds(ends),
  }
  if (freq === 'weekly' || freq === 'fortnightly') rule.byday = byday.length ? byday : undefined
  return JSON.stringify(rule)
}

// Keep only the fields relevant to the chosen end type (so a stale date/count
// from a toggled-away option isn't persisted).
const normaliseEnds = (ends: RecurrenceEnds): RecurrenceEnds => {
  if (ends.type === 'on' && ends.date) return { type: 'on', date: ends.date }
  if (ends.type === 'after' && ends.count && ends.count >= 1) return { type: 'after', count: Math.floor(ends.count) }
  return { type: 'never' }
}

// The recurrenceEndAt column mirrors an "ends: on <date>" bound (end of that
// day, ISO) so the read paths can cheaply pre-filter series by range; null for
// never/after (after-N is enforced during expansion). LOCAL end-of-day so the
// stored instant matches the user's wall-clock intent.
export const recurrenceEndAtFor = (ends: RecurrenceEnds): string | null => {
  if (ends.type !== 'on' || !ends.date) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ends.date)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999).toISOString()
}

// Parse a stored rule back into form state (edit mode). Returns null for
// absent/invalid rules (→ the form shows "Does not repeat").
export const parseRecurrenceRule = (raw: string | null | undefined): RecurrenceRule | null => {
  if (typeof raw !== 'string' || !raw.trim()) return null
  try {
    const r = JSON.parse(raw) as RecurrenceRule
    if (!r || !['daily', 'weekly', 'fortnightly', 'monthly'].includes(r.freq)) return null
    return {
      freq: r.freq,
      interval: typeof r.interval === 'number' && r.interval >= 1 ? r.interval : 1,
      byday: Array.isArray(r.byday) ? r.byday : undefined,
      ends: r.ends && typeof r.ends === 'object' ? r.ends : { type: 'never' },
    }
  } catch {
    return null
  }
}

// One-line human summary for the form (e.g. "Weekly on Mon, Wed").
export const summariseRule = (freq: RecurrenceFreq, byday: Weekday[], ends: RecurrenceEnds): string => {
  const dayLabels = byday
    .map((c) => WEEKDAYS.find((w) => w.code === c)?.label)
    .filter(Boolean)
    .join(', ')
  let base: string
  switch (freq) {
    case 'daily':
      base = 'Every day'
      break
    case 'weekly':
      base = dayLabels ? `Weekly on ${dayLabels}` : 'Weekly'
      break
    case 'fortnightly':
      base = dayLabels ? `Every 2 weeks on ${dayLabels}` : 'Every 2 weeks'
      break
    case 'monthly':
      base = 'Monthly'
      break
  }
  if (ends.type === 'on' && ends.date) base += `, until ${ends.date}`
  else if (ends.type === 'after' && ends.count) base += `, ${ends.count} times`
  return base
}
